import React, { useCallback, useEffect, useState } from 'react';
import { AsyncTypeahead, Highlighter, InputProps, Menu, MenuItem } from "react-bootstrap-typeahead";
import { Producer, getProducerDisplayName } from "features/settings/producers/producer";
import producerService from "features/settings/producers/producerService";
import './CustomerAccountPicker.scss';
import { ProducerAccount, getProducerAccountDisplayName } from './producerAccount';
import { localizeProducer } from 'features/localization/localizeService';
import useCache from 'common/useCache';

interface CustomerAccountPickerProps {
    onCustomerSelected(customer: Producer, account?: ProducerAccount): void;
    selected?: Nullish<Producer>;
    inputProps?: InputProps;
    selectedAccount?: string;
    showClearButton?: boolean;
}

interface CustomerOption {
    customer: Producer;
    account?: ProducerAccount;
    disabled: boolean;
}
const CustomerAccountPicker: React.FC<CustomerAccountPickerProps> = props => {

    const [customers, setCustomers] = useState<Producer[]>([]);
    const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
    const [searchedText, setSearchedText] = useState<string>(null);

    const cache = useCache<Producer[]>();

    useEffect(() => {
        if (props.selected == null) {
            setCustomers([]);
            setSearchedText(null);
        }
    }, [props.selected])

    const customerOptions = customers.flatMap(m => buildCustomerOption(m));

    if (customerOptions.length === 0 && props.selected != null) {
        customerOptions.push(...buildCustomerOption(props.selected))
    }

    const selectedOption = customerOptions.find(m => m.customer.id === props.selected?.id && m.account?.accountId === props.selectedAccount);

    const doSearch = useCallback(query => {
        searchCustomers(query);
    }, []);


    function buildCustomerOption(customer: Producer): CustomerOption[] {
        const parentOption: CustomerOption = {
            customer: customer,
            account: null,
            disabled: customer.accounts.length > 0
        };
        return [
            parentOption,
            ...customer.accounts.map(a => ({ ...parentOption, account: a, disabled: false }))
        ];
    }

    async function searchCustomers(search: string) {
        setIsSearchingCustomers(true);
        try {
            if (search && search?.trim() !== '') {
                setSearchedText(search);
                const result = await getSearchResults(search);
                setCustomers(result);
            }
        }
        finally {
            setIsSearchingCustomers(false);
        }
    }

    const customerPageSize = 100;

    function includesText(value: Nullish<string>, search: string): boolean {
        return (value ?? "").toLowerCase().includes(search.toLowerCase());
    }

    async function getSearchResults(search: string): Promise<Producer[]> {
        const cachedResult = cache.get(search);
        if (cachedResult != null) return cachedResult;
        const result = await producerService.listProducersV2(customerPageSize, 0, null, null, search, true, null);

        const filtered: Producer[] = (result.data ?? []).map(p => ({
            ...p,
            accounts: (p.accounts ?? []).filter(a => a?.isPrimaryUser === true),
        }))
            .filter(p => !search?.trim()
                || p.useBusinessName && includesText(p.businessName, search)
                || (!p.useBusinessName && (includesText(p.firstName, search) || includesText(p.lastName, search)))
                || (p.accounts ?? []).some(a => includesText(a?.accountId, search))
            );

        cache.set(search, filtered);
        return filtered;
    }

    return <div className="customer-account-picker" data-testid="customer-account-picker">
        <AsyncTypeahead
            id="autoComplete-picklist"
            inputProps={{ ...props.inputProps }}
            placeholder={localizeProducer("CustomerAccountPicker_Placeholder_ChooseCustomer")}
            labelKey={(m: CustomerOption) => m.account ? `${getProducerDisplayName(m.customer)} - ${getProducerAccountDisplayName(m.account)}` : `${getProducerDisplayName(m.customer)}`}
            filterBy={() => true}
            renderMenu={(o, menuProps) => {
                return (
                    <Menu {...menuProps}>
                        {customerOptions.map((opt, idx) => (
                            opt.account == null
                                ? <MenuItem key={opt.customer.id}
                                    option={opt}
                                    position={idx}
                                    disabled={opt.disabled}
                                >
                                    <Highlighter search={searchedText}>
                                        {getProducerDisplayName(opt.customer)}
                                    </Highlighter>
                                </MenuItem>
                                :
                                <MenuItem key={`${opt.customer.id}-${opt.account.accountId}`}
                                    className="shifted"
                                    option={opt}
                                    position={idx}
                                >
                                    <Highlighter search={searchedText}>
                                        {`${getProducerAccountDisplayName(opt.account)}`}
                                    </Highlighter>
                                </MenuItem>)
                        )}
                    </Menu>
                )
            }}
            options={customerOptions}
            paginate={false}

            maxResults={customerPageSize}
            minLength={0}
            clearButton={props.showClearButton}
            selected={selectedOption == null ? null : [selectedOption]}
            onChange={selectedVals => {
                const selected = selectedVals == null ? null : selectedVals[0];
                props.onCustomerSelected(selected?.customer, selected?.account);
            }}

            onSearch={doSearch}
            isLoading={isSearchingCustomers}
            delay={150}
            useCache={false}
        />
    </div>
}


export default CustomerAccountPicker;